import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { AuthService } from '@shared/services/auth.service';
import { Router } from '@angular/router';
import {
  BroadcastChannelService,
  MessageTypes,
} from '@shared/services/broadcast-channel.service';
import { tap } from 'rxjs';
import { AuthStateModel } from './auth.typings';
import {
  Login,
  Logout,
  RefreshAuth,
  SetUserActiveDivision,
  SetUserDivisions,
  SyncState,
} from './auth.actions';
import { RouteNames } from '../../route-names';

const authStateBroadcastChannel = new BroadcastChannel('ngxs-sync');

const initialState = () => {
  return {
    greenland: false,
    jwtToken: null,
    user: null,
    refreshToken: null,
    role: null,
    divisions: [],
  };
};
@State<AuthStateModel>({
  name: 'auth',
  defaults: initialState(),
})
@Injectable()
export class AuthState {
  constructor(
    private readonly auth: AuthService,
    private readonly store: Store,
    private readonly router: Router,
    private readonly broadcastChannel: BroadcastChannelService,
  ) {
    authStateBroadcastChannel.addEventListener(
      'message',
      (event: MessageEvent<AuthStateModel>) => {
        console.log(event);
        this.store.dispatch(new SyncState(event.data));
      },
    );
  }

  @Action(SyncState)
  syncState(ctx: StateContext<AuthStateModel>, action: SyncState) {
    ctx.setState(action.state);
  }

  @Action(SetUserDivisions)
  setUserDivisions(ctx: StateContext<AuthStateModel>) {
    return this.auth.getUserDivisions().pipe(
      tap(divisions => {
        ctx.patchState({
          divisions,
        });
        authStateBroadcastChannel.postMessage(ctx.getState());
      }),
    );
  }

  @Action(SetUserActiveDivision)
  setUserActiveDivision(
    ctx: StateContext<AuthStateModel>,
    action: SetUserActiveDivision,
  ) {
    const state = ctx.getState();
    ctx.patchState({
      user: state.user
        ? {
            ...state.user,
            activeDivisionId: action.id,
          }
        : null,
    });
  }

  @Action(Login)
  login(ctx: StateContext<AuthStateModel>, action: Login) {
    return this.auth.login(action.credentials).pipe(
      tap(data => {
        ctx.patchState({
          greenland: data.greenland,
          jwtToken: data.jwtToken,
          refreshToken: data.refreshToken,
          user: data.user,
          role: data.role,
        });

        authStateBroadcastChannel.postMessage(ctx.getState());

        this.broadcastChannel.postMessage({
          type: MessageTypes.NAVIGATE_HOME,
        });
      }),
    );
  }

  @Action(RefreshAuth)
  refreshAuth(ctx: StateContext<AuthStateModel>, action: RefreshAuth) {
    const data = action.auth;
    ctx.patchState({
      greenland: data.greenland,
      jwtToken: data.jwtToken,
      user: data.user,
      refreshToken: data.refreshToken,
      role: data.role,
    });
    authStateBroadcastChannel.postMessage(ctx.getState());
  }

  @Action(Logout)
  logout(ctx: StateContext<AuthStateModel>) {
    ctx.patchState(initialState());
    authStateBroadcastChannel.postMessage(ctx.getState());
    this.router.navigate([RouteNames.LOGIN_PAGE]);
    this.broadcastChannel.postMessage({
      type: MessageTypes.NAVIGATE_LOGIN,
    });
  }

  @Selector()
  static getUserDivisions(state: AuthStateModel) {
    return state.divisions;
  }

  @Selector()
  static getUser(state: AuthStateModel) {
    return state.user;
  }

  @Selector()
  static getToken(state: AuthStateModel) {
    return state.jwtToken;
  }

  @Selector()
  static getRefreshToken(state: AuthStateModel) {
    return state.refreshToken;
  }

  @Selector()
  static isAuthenticated(state: AuthStateModel) {
    return !!state.user?.email;
  }

  @Selector()
  static isGreenlandUser(state: AuthStateModel) {
    return state.greenland;
  }

  @Selector()
  static getUserActiveDivision(state: AuthStateModel) {
    return state.user?.activeDivisionId;
  }
}
